import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { getDatasourcesDocumentsTableIcon } from '@/modules/collections/helper';
import { DatasourceDocument } from '@/modules/collections/types';
import { COLUMN_PRESETS } from '@/util/constants';
import { greekAwareFilter } from '@/util/greekFilter';

export const columns = (): ColumnDef<DatasourceDocument>[] => [
  {
    id: 'select',
    header: ({ table }) => {
      const getIsChecked = () => {
        if (table.getIsAllPageRowsSelected()) {
          return true;
        } else if (table.getIsSomePageRowsSelected()) {
          return 'indeterminate';
        }
        return false;
      };

      return (
        <div className="flex h-8 w-8 items-center justify-center">
          <Checkbox
            checked={getIsChecked()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
            aria-label={t('common.select_all_rows')}
          />
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex h-8 w-8 items-center justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
            aria-label={t('common.select_row')}
          />
        </div>
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
  {
    id: 'type',
    accessorKey: 'datasource_type',
    header: t('common.type'),
    // This is a custom filter function for the type column
    // It is used to filter the rows with 2 values -> zendesk_kb and intercom_kb
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue || filterValue.length === 0) {
        return true;
      }

      // If filterValue is an array, check if row value is included
      if (Array.isArray(filterValue)) {
        return filterValue.includes(row.getValue(columnId));
      }

      // Single value comparison
      return row.getValue(columnId) === filterValue;
    },
    cell: ({ row }) => {
      const Icon = getDatasourcesDocumentsTableIcon(row.original);

      return (
        <div className="flex items-center justify-center">
          <Icon size={16} className="text-foreground-muted" />
        </div>
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
  {
    id: 'name',
    accessorKey: 'name',
    filterFn: greekAwareFilter,
    header: ({ column }) => (
      <SortableHeader column={column} className="pl-2">
        {t('common.name')}
      </SortableHeader>
    ),
    cell: ({ row }) => {
      return (
        <div className="min-w-0 flex-col pl-2">
          <span className="block truncate font-medium">{row.original.name}</span>
        </div>
      );
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  },
];
